import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, CircleCheck, CircleX, Copy } from 'lucide-react';
import {
  CurrentMerchantForDnsRecordsQuery,
  VerifyCnameRecordMutation,
  VerifyTxtRecordMutation,
  DnsRecordType
} from '@/gql/graphql';
import { Logo } from '../../components/logos/logo';

// Queries for verification
const VERIFY_TXT_RECORD_MUTATION = gql`
  mutation VerifyTxtRecord {
    verifyTxtRecord {
      id
      type
      name
      value
      verified
    }
  }
`;

const VERIFY_CNAME_RECORD_MUTATION = gql`
  mutation VerifyCnameRecord {
    verifyCnameRecord {
      id
      type
      name
      value
      verified
    }
  }
`;

const CURRENT_MERCHANT_QUERY = gql`
  query CurrentMerchantForDnsRecords {
    currentMerchant {
      id
      websiteUrl
      dnsVerificationRecords {
        id
        type
        name
        value
        verified
      }
    }
  }
`;

type DNSRecord = NonNullable<
  NonNullable<CurrentMerchantForDnsRecordsQuery['currentMerchant']>['dnsVerificationRecords']
>[number];

export function DNSRecords() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [verifyTxtRecord, { called: txtCalled }] = useMutation<VerifyTxtRecordMutation>(
    VERIFY_TXT_RECORD_MUTATION
  );
  const [verifyCnameRecord, { called: cnameCalled }] = useMutation<VerifyCnameRecordMutation>(
    VERIFY_CNAME_RECORD_MUTATION
  );
  const { data: currentMerchantData, loading: currentMerchantLoading } =
    useQuery<CurrentMerchantForDnsRecordsQuery>(CURRENT_MERCHANT_QUERY);

  const handleContinue = () => {
    void navigate('../setup/consent');
  };

  const handleVerifyRecord = (record: DNSRecord) => {
    if (record.type === DnsRecordType.Txt) {
      void verifyTxtRecord();
    } else {
      void verifyCnameRecord();
    }
  };

  const handleVerifyRecords = () => {
    currentMerchantData?.currentMerchant.dnsVerificationRecords.map((record: DNSRecord) => {
      const isVerifed = isRecordVerified(record);
      if (!isVerifed) {
        handleVerifyRecord(record);
      }
    });
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard!',
      variant: 'default'
    });
  };

  const handleSkip = () => {
    toast({
      title: 'DNS verification skipped',
      description: 'You can always come back to verify your DNS records later.',
      variant: 'default'
    });
    void navigate('../setup/consent');
  };

  const isRecordVerified = (record: DNSRecord) => {
    const txtRecord = currentMerchantData?.currentMerchant.dnsVerificationRecords.find(
      (n: DNSRecord) => n.type === DnsRecordType.Txt
    );
    const cnameRecord = currentMerchantData?.currentMerchant.dnsVerificationRecords.find(
      (n: DNSRecord) => n.type === DnsRecordType.Cname
    );
    return record.type === DnsRecordType.Txt ? txtRecord?.verified : cnameRecord?.verified;
  };

  const areAllRecordsVerified = () => {
    return currentMerchantData?.currentMerchant.dnsVerificationRecords.every(
      (record: DNSRecord) => {
        return isRecordVerified(record);
      }
    );
  };

  return (
    <Card className="w-full max-w-xl mx-4 sm:mx-auto p-9 rounded-[1rem]">
      <ArrowLeft
        className="cursor-pointer"
        onClick={() => {
          void navigate(-1);
        }}
      />
      <CardHeader className="items-center space-y-8 p-0">
        <Logo icon="contextual" width={36} height={36} />
        <h1 className="ttext-lg sm:text-xl md:text-2xl lg:text-3xl font-medium">
          Connect your store
        </h1>
      </CardHeader>
      <CardContent className="my-9 p-0">
        <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold mb-2 sm:mb-4 ">
          Setup your DNS Records
        </h2>
        <p className="text-xs sm:text-xs md:text-sm lg:text-base text-gray-600">
          Add these DNS records to your domain configuration to authenticate your setup as
          first-party.
        </p>

        <br />
        <p className="text-xs sm:text-xs md:text-sm lg:text-base text-gray-600">
          Please note it may take some time for the records to propagate so feel free to skip ahead
          if you’re confident you entered them correctly.
        </p>
        {currentMerchantLoading ? (
          <p>Loading DNS records...</p>
        ) : currentMerchantData?.currentMerchant.dnsVerificationRecords ? (
          currentMerchantData.currentMerchant.dnsVerificationRecords.map(
            (record: DNSRecord, index: number) => (
              <>
                <div key={index} className="rounded-lg">
                  <div className="space-y-2 rounded-xl	border border-gray-300 p-4 my-6">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold py-2 text-sm sm:text-base md:text-lg lg:text-xl">
                          {record.type} Record
                        </h3>
                        {record.type === DnsRecordType.Txt && (
                          <Badge
                            variant="outline"
                            className="bg-[hsl(var(--card))] text-gray-500 font-normal"
                          >
                            Optional
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant="outline"
                          className={`px-2 py-1 border-none rounded-lg text-xs ${
                            isRecordVerified(record)
                              ? 'text-greenDark bg-cyan'
                              : 'text-orangeLight bg-pinkLight'
                          } font-light`}
                        >
                          {isRecordVerified(record) ? 'Verified' : 'Not Verified'}
                          {isRecordVerified(record) ? (
                            <CircleCheck className="ml-2 h-4 w-4" />
                          ) : (
                            <CircleX className="ml-2 h-4 w-4" />
                          )}
                        </Badge>
                      </div>
                    </div>
                    {[
                      { label: 'Name', value: record.name, canCopy: true },
                      { label: 'Value', value: record.value, canCopy: true }
                    ].map(({ label, value, canCopy }) => (
                      <div key={label}>
                        <span className="w-16 text-sm text-gray-600 flex-shrink-0">{label}</span>
                        <div className="flex-grow flex items-center gap-8">
                          <Input
                            value={value}
                            readOnly
                            className="bg-[hsl(var(--card))] flex-grow"
                          />
                          {canCopy && (
                            <Button
                              onClick={() => void copyToClipboard(value)}
                              variant="secondary"
                              size="sm"
                            >
                              <Copy className="mr-2 h-4 w-4" />
                              Copy
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {!isRecordVerified(record) &&
                    (record.type === DnsRecordType.Txt ? txtCalled : cnameCalled) && (
                      <Alert variant="destructive" className="mt-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Verification Error</AlertTitle>
                        <AlertDescription>{`The record could not be verified. Please try again later.`}</AlertDescription>
                      </Alert>
                    )}
                </div>
              </>
            )
          )
        ) : (
          <p>No DNS records found.</p>
        )}
      </CardContent>
      <CardFooter className="flex justify-center space-x-4 sm:space-x-6 pt-0 sm:pt-4">
        {areAllRecordsVerified() ? (
          <Button onClick={handleContinue} disabled={!areAllRecordsVerified()} className="">
            Save & Continue
          </Button>
        ) : (
          <Button
            onClick={() => {
              handleVerifyRecords();
            }}
            className=""
          >
            Refresh
          </Button>
        )}
        <Button onClick={handleSkip} variant="secondary" className="">
          Skip DNS Verification
        </Button>
      </CardFooter>
    </Card>
  );
}
